import { useMemo, useState } from 'react';
import { Box, Checkbox, FormControlLabel, Stack, Tooltip } from '@mui/material';
import type {
  InvestorListDTO,
  PreviewInvestmentTransactionsImportResponse,
  TransactionImportRowOverrideDTO,
} from '@inversor-hub/types';
import { PreviewInfoBanner } from './PreviewInfoBanner';
import { PreviewSummaryChips } from './PreviewSummaryChips';
import { PreviewTable, type PreviewTableRowEntry } from './PreviewTable';
import { PreviewFooter } from './PreviewFooter';
import {
  derivePreviewStatus,
  isImportableStatus,
  matchesFilter,
  type PreviewFilter,
  type PreviewSummary,
} from './types';

interface PreviewStepProps {
  preview: PreviewInvestmentTransactionsImportResponse['data'];
  investments: InvestorListDTO[];
  showInvestmentColumn: boolean;
  showRoomColumn: boolean;
  roomRentInvestmentIds: string[];
  rowOverrides: TransactionImportRowOverrideDTO[];
  includeDuplicates: boolean;
  rowOverridesDirty: boolean;
  isRefreshing: boolean;
  isConfirming: boolean;
  onIncludeDuplicatesChange: (value: boolean) => void;
  onRowOverrideChange: (
    rowNumber: number,
    patch: Omit<TransactionImportRowOverrideDTO, 'rowNumber'>
  ) => void;
  onRefreshPreview: () => void;
  onConfirm: () => void;
  onBack: () => void;
  onCancel: () => void;
  isBusy: boolean;
}

export const PreviewStep = ({
  preview,
  investments,
  showInvestmentColumn,
  showRoomColumn,
  roomRentInvestmentIds,
  rowOverrides,
  includeDuplicates,
  rowOverridesDirty,
  isRefreshing,
  isConfirming,
  onIncludeDuplicatesChange,
  onRowOverrideChange,
  onRefreshPreview,
  onConfirm,
  onBack,
  onCancel,
  isBusy,
}: PreviewStepProps) => {
  const [filter, setFilter] = useState<PreviewFilter>('all');
  const [showHelp, setShowHelp] = useState(true);

  const overridesByRow = useMemo(() => {
    const map = new Map<number, TransactionImportRowOverrideDTO>();
    rowOverrides.forEach((override) => map.set(override.rowNumber, override));
    return map;
  }, [rowOverrides]);

  const entries: PreviewTableRowEntry[] = useMemo(
    () =>
      preview.previewRows.map((row) => {
        const override = overridesByRow.get(row.rowNumber);
        return {
          row,
          override,
          status: derivePreviewStatus(row, override),
        };
      }),
    [preview.previewRows, overridesByRow]
  );

  const summary = useMemo<PreviewSummary>(() => {
    let importable = 0;
    let withWarnings = 0;
    let blocked = 0;
    let skipped = 0;
    let duplicates = 0;
    entries.forEach(({ row, status }) => {
      if (row.duplicate) duplicates += 1;
      if (status === 'skipped') skipped += 1;
      if (status === 'needs_review') withWarnings += 1;
      if (status === 'blocked') blocked += 1;
      if (isImportableStatus(status, includeDuplicates)) importable += 1;
    });
    return {
      total: entries.length,
      importable,
      withWarnings,
      blocked,
      skipped,
      duplicates,
    };
  }, [entries, includeDuplicates]);

  const visibleEntries = useMemo(
    () =>
      entries.filter(({ row, status }) => matchesFilter(filter, row, status, includeDuplicates)),
    [entries, filter, includeDuplicates]
  );

  const hasDuplicates = summary.duplicates > 0;

  return (
    <Stack spacing={1.5} sx={{ flex: 1, minHeight: 0 }}>
      <PreviewInfoBanner open={showHelp} onClose={() => setShowHelp(false)} />

      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        alignItems={{ xs: 'stretch', sm: 'center' }}
        justifyContent="space-between"
        gap={1}
      >
        <PreviewSummaryChips filter={filter} summary={summary} onFilterChange={setFilter} />

        {hasDuplicates && (
          <Tooltip title="Los posibles duplicados se detectan comparando fecha, importe y concepto con transacciones ya existentes.">
            <FormControlLabel
              sx={{ m: 0, whiteSpace: 'nowrap', color: 'text.secondary' }}
              control={
                <Checkbox
                  size="small"
                  checked={includeDuplicates}
                  disabled={isRefreshing}
                  onChange={(event) => onIncludeDuplicatesChange(event.target.checked)}
                />
              }
              label="Importar también duplicados"
            />
          </Tooltip>
        )}
      </Stack>

      <Box sx={{ flex: 1, minHeight: 240, display: 'flex', flexDirection: 'column' }}>
        <PreviewTable
          entries={visibleEntries}
          investments={investments}
          roomOptions={preview.roomOptions}
          showInvestmentColumn={showInvestmentColumn}
          showRoomColumn={showRoomColumn}
          roomRentInvestmentIds={roomRentInvestmentIds}
          isLoading={isRefreshing}
          onRowOverrideChange={onRowOverrideChange}
        />
      </Box>

      <PreviewFooter
        importableCount={summary.importable}
        warningsCount={summary.withWarnings}
        blockedCount={summary.blocked}
        isDirty={rowOverridesDirty}
        isRefreshing={isRefreshing}
        isConfirming={isConfirming}
        isBusy={isBusy}
        onRefresh={onRefreshPreview}
        onConfirm={onConfirm}
        onBack={onBack}
        onCancel={onCancel}
      />
    </Stack>
  );
};
